import {
  Ach,
  AdyenCheckout,
  AdyenCheckoutError,
  Card,
  CoreConfiguration,
  Dropin,
  DropinConfiguration,
  SubmitData,
} from '@adyen/adyen-web';
import {
  getCountryCode,
  HostedPaymentSessionPayload,
  LocaleCode,
  PspSessionResponsePayload,
} from '@appliedsystems/payments-core';
import { v4 as uuidv4 } from 'uuid';
import { TranslateFunc } from '../hooks/usePaymentsTranslation';
import { getConfig } from './config';

// Example of how the AdyenCheckoutState should look like
// export type AdyenCheckoutState = {
//   data: {
//     riskData?: {
//       clientData: string;
//     };
//     // paymentMethod: {
//     //   type: AdyenPaymentMethod;
//     //   checkoutAttemptId?: string;

//     //   // ach info
//     //   encryptedBankAccountNumber?: string;
//     //   encryptedBankLocationId?: string;
//     //   ownerName?: string;

//     //   // card info
//     //   holderName?: string;
//     //   encryptedCardNumber?: string;
//     //   encryptedExpiryMonth?: string;
//     //   encryptedExpiryYear?: string;
//     //   encryptedSecurityCode?: string;
//     //   brand?: string;

//     //   // eft info
//     //   bankAccountNumber?: string;
//     //   bankLocationId?: string;
//     //   bankCode?: string;
//     // };
//     browserInfo?: {
//       acceptHeader: string;
//       colorDepth: number;
//       language: string;
//       javaEnabled: boolean;
//       screenHeight: number;
//       screenWidth: number;
//       userAgent: string;
//       timeZoneOffset: number;
//     };
//     origin?: string;
//     clientStateDataIndicator?: boolean;
//     storePaymentMethod?: boolean;
//   };
//   isValid: boolean;
// };

export type AdyenCheckoutState = SubmitData; // This is the type that Adyen uses for the state in their documentation

export type AdyenConfiguration = {
  beforeSubmit: CoreConfiguration['beforeSubmit'];
  onReady: () => void;
  onPaymentCompleted: (result: any, component: any) => void;
  onError: (error: AdyenCheckoutError, component?: Dropin) => void;
  onSelect: (paymentMethod: 'card' | 'ach') => void;
  onSubmit: (state: AdyenCheckoutState, component: Dropin) => void;
  onAdditionalDetails: (state: any, component: any) => void;
  onDisableStoredPaymentMethod?: (recurringDetailReference: string, resolve: () => void, reject: () => void) => void;
  showStoredPaymentMethods?: boolean;
  showPayButton?: boolean;
  allowPaymentMethods?: string[];
  enableStoreDetails?: boolean;
  locale?: string;
};

export const initAdyenCheckout = async (
  domNode: string | HTMLElement,
  sessionDetails: Pick<PspSessionResponsePayload | HostedPaymentSessionPayload, 'pspSessionId' | 'sessionData'>,
  t: TranslateFunc,
  config?: Partial<AdyenConfiguration>,
) => {
  const checkout = await AdyenCheckout({
    environment: getConfig('REACT_APP_ADYEN_ENVIRONMENT'),
    clientKey: getConfig('REACT_APP_ADYEN_CLIENT_KEY'),
    session: {
      id: sessionDetails.pspSessionId,
      sessionData: sessionDetails.sessionData,
    },
    showPayButton: config?.showPayButton ?? true,
    beforeSubmit: config?.beforeSubmit,
    onPaymentCompleted: config?.onPaymentCompleted,
    onError: config?.onError,
    onSubmit: config?.onSubmit,
    // TODO: Need to wire up to handle actions like 3DSecure
    onAdditionalDetails: config?.onAdditionalDetails,
    allowPaymentMethods: config?.allowPaymentMethods,
    locale: config?.locale,
    countryCode: getCountryCode(config?.locale as LocaleCode),
    translations: {
      'en-US': {
        confirmPurchase: t('PAY'),
        'creditCard.cardNumber.label': t('CREDIT_CARD_NUMBER'),
        'creditCard.expiryDate.label': t('EXPIRATION_DATE'),
        'creditCard.securityCode.label': t('SECURITY_CODE'),
        'creditCard.expiryDate.contextualText': t('DATE_INSTRUCTION'),
        'creditCard.securityCode.contextualText.3digits': t('CVC_INSTRUCTION'),
        'cc.num.900': t('CARD_NUMBER_ERROR'),
        'cc.dat.910': t('EXPIRY_DATE_ERROR'),
        'cc.cvc.920': t('CVC_ERROR'),
        'form.instruction': t('FORM_INSTRUCTIONS'),
        'creditCard.holderName': t('NAME_ON_CARD'),
      },
      'en-CA': {
        confirmPurchase: t('PAY'),
        'creditCard.cardNumber.label': t('CREDIT_CARD_NUMBER'),
        'creditCard.expiryDate.label': t('EXPIRATION_DATE'),
        'creditCard.securityCode.label': t('SECURITY_CODE'),
        'creditCard.expiryDate.contextualText': t('DATE_INSTRUCTION'),
        'creditCard.securityCode.contextualText.3digits': t('CVC_INSTRUCTION'),
        'cc.num.900': t('CARD_NUMBER_ERROR'),
        'cc.dat.910': t('EXPIRY_DATE_ERROR'),
        'cc.cvc.920': t('CVC_ERROR'),
        'form.instruction': t('FORM_INSTRUCTIONS'),
        'creditCard.holderName': t('NAME_ON_CARD'),
      },
      'en-GB': {
        confirmPurchase: t('PAY'),
        'creditCard.cardNumber.label': t('CREDIT_CARD_NUMBER'),
        'creditCard.expiryDate.label': t('EXPIRATION_DATE'),
        'creditCard.securityCode.label': t('SECURITY_CODE'),
        'creditCard.expiryDate.contextualText': t('DATE_INSTRUCTION'),
        'creditCard.securityCode.contextualText.3digits': t('CVC_INSTRUCTION'),
        'cc.num.900': t('CARD_NUMBER_ERROR'),
        'cc.dat.910': t('EXPIRY_DATE_ERROR'),
        'cc.cvc.920': t('CVC_ERROR'),
        'form.instruction': t('FORM_INSTRUCTIONS'),
        'creditCard.holderName': t('NAME_ON_CARD'),
      },
      'fr-CA': {
        confirmPurchase: t('PAY'),
        'creditCard.cardNumber.label': t('CREDIT_CARD_NUMBER'),
        'creditCard.expiryDate.label': t('EXPIRATION_DATE'),
        'creditCard.securityCode.label': t('SECURITY_CODE'),
        'creditCard.expiryDate.contextualText': t('DATE_INSTRUCTION'),
        'creditCard.securityCode.contextualText.3digits': t('CVC_INSTRUCTION'),
        'cc.num.900': t('CARD_NUMBER_ERROR'),
        'cc.dat.910': t('EXPIRY_DATE_ERROR'),
        'cc.cvc.920': t('CVC_ERROR'),
        'form.instruction': t('FORM_INSTRUCTIONS'),
        'creditCard.holderName': t('NAME_ON_CARD'),
      },
    },
  });

  const dropinElementProps: DropinConfiguration = {
    instantPaymentTypes: [],
    openFirstPaymentMethod: true,
    showStoredPaymentMethods: config?.showStoredPaymentMethods ?? true,
    showRemovePaymentMethodButton: true,
    onDisableStoredPaymentMethod: config?.onDisableStoredPaymentMethod,
    onReady: config?.onReady,
    onSelect: (uiElement) => {
      config?.onSelect?.(uiElement.type as 'card' | 'ach');
    },
  };

  const component = new Dropin(checkout, {
    ...dropinElementProps,
    showPaymentMethods: true,
    paymentMethodComponents: [Card, Ach],
    paymentMethodsConfiguration: {
      storedCard: {
        amount: undefined,
      },
      card: {
        hasHolderName: true,
        holderNameRequired: false,
        amount: undefined, // hides the amount in the pay button
        billingAddressRequired: false,
        enableStoreDetails: config?.enableStoreDetails,
        label: 'ABC Company',
        name: t('CARDS'),
      },
      ach: {
        hasHolderName: true,
        // Keep this ts-ignore as billingAddressRequired and holderNameRequired is not in the adyen types
        // It's working as expected and billing address and holder name is not required
        // @ts-ignore
        holderNameRequired: false,
        billingAddressRequired: false,
        enableStoreDetails: config?.enableStoreDetails,
      },
    },
  }).mount(domNode);

  return component;
};

export const generateIdempotencyKey = (): string => {
  return uuidv4();
};
